// src/pages/Searches.js
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { formatDate, getErrorMessage } from '../utils/helpers';
import { PageWrapper } from './Dashboard';

const occupantText = (list) =>
  (list || []).map(o => (o.bed ? `${o.name} (${o.bed})` : o.name)).join(' · ');

export default function Searches() {
  const { staff, isSupervisor, isAdmin } = useAuth();
  const canSupervise = isSupervisor || isAdmin;

  const [tab, setTab] = useState('mine');
  const [mine, setMine] = useState([]);
  const [lockedCount, setLockedCount] = useState(0);
  const [dayList, setDayList] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [pendingVerify, setPendingVerify] = useState([]);
  const [flags, setFlags] = useState([]);
  const [clients, setClients] = useState([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeSearch, setActiveSearch] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const flash = (msg) => { setSuccess(msg); setTimeout(() => setSuccess(''), 4000); };

  const loadMine = useCallback(async () => {
    try {
      const r = await api.get('/searches/my-searches');
      setMine(r.data.searches || []);
      setLockedCount(r.data.lockedCount || 0);
    } catch (err) { setError(getErrorMessage(err)); }
  }, []);

  const loadSupervisor = useCallback(async () => {
    if (!canSupervise) return;
    try {
      const [d, pv, rm, sh, st, fl, cl] = await Promise.all([
        api.get(`/searches?date=${date}`).catch(() => ({ data: [] })),
        api.get('/searches/pending-verification').catch(() => ({ data: [] })),
        api.get('/searches/rooms').catch(() => ({ data: [] })),
        api.get('/schedules/shifts').catch(() => ({ data: [] })),
        api.get('/staff').catch(() => ({ data: [] })),
        api.get('/searches/flags').catch(() => ({ data: [] })),
        api.get('/clients').catch(() => ({ data: [] })),
      ]);
      setDayList(d.data || []);
      setPendingVerify(pv.data || []);
      setRooms(rm.data || []);
      setShifts(sh.data || []);
      setStaffList((st.data || []).filter(s => s.is_active));
      setFlags(fl.data || []);
      setClients(cl.data || []);
    } catch (err) { setError(getErrorMessage(err)); }
  }, [canSupervise, date]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const s = await api.get('/searches/statuses').catch(() => ({ data: [] }));
        setStatuses(s.data || []);
        await loadMine();
        await loadSupervisor();
      } finally { setLoading(false); }
    })();
  }, [loadMine, loadSupervisor]);

  const openSearch = async (id) => {
    setError('');
    try {
      const r = await api.get(`/searches/${id}`);
      setActiveSearch(r.data);
      setForm({
        statusCode: r.data.status_code === 'pending' ? 'completed' : r.data.status_code,
        clientPresent: r.data.client_present === null ? true : r.data.client_present,
        contrabandFound: !!r.data.contraband_found,
        contrabandDescription: r.data.contraband_description || '',
        disposition: r.data.disposition || 'Secured for discharge',
        incidentReportFiled: !!r.data.incident_report_filed,
        statusNote: '',
      });
    } catch (err) { setError(getErrorMessage(err)); }
  };

  const submitSearch = async () => {
    setSaving(true); setError('');
    try {
      await api.patch(`/searches/${activeSearch.id}/complete`, form);
      setActiveSearch(null);
      await loadMine(); await loadSupervisor();
      flash('Search logged.');
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const deferSearch = async (reason) => {
    setSaving(true); setError('');
    try {
      await api.patch(`/searches/${activeSearch.id}/defer`, { reason });
      setActiveSearch(null);
      await loadMine(); await loadSupervisor();
      flash('Deferred to next shift.');
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const generate = async (shiftId) => {
    setSaving(true); setError('');
    try {
      const r = await api.post('/searches/generate', { shiftId, searchDate: date });
      await loadSupervisor(); await loadMine();
      flash(`${r.data.created} room(s) drawn.`);
    } catch (err) { setError(getErrorMessage(err)); }
    finally { setSaving(false); }
  };

  const assign = async (id, staffId) => {
    try {
      await api.patch(`/searches/${id}/assign`, { staffId: staffId || null });
      await loadSupervisor(); flash('Assignment updated.');
    } catch (err) { setError(getErrorMessage(err)); }
  };

  const forCause = async (payload) => {
    setSaving(true); setError('');
    try {
      await api.post('/searches/for-cause', payload);
      await loadSupervisor(); await loadMine();
      flash('For-cause search created.');
      return true;
    } catch (err) { setError(getErrorMessage(err)); return false; }
    finally { setSaving(false); }
  };

  const deleteSearch = async (id) => {
    if (!window.confirm('Remove this search from the draw? Only possible before it is worked.')) return;
    try { await api.delete(`/searches/${id}`); await loadSupervisor(); flash('Search removed.'); }
    catch (err) { setError(getErrorMessage(err)); }
  };

  const addAmendment = async (id, text) => {
    try { await api.post(`/searches/${id}/amend`, { amendment: text }); flash('Amendment added.'); return true; }
    catch (err) { setError(getErrorMessage(err)); return false; }
  };

  const verify = async (id, notes) => {
    try {
      await api.patch(`/searches/${id}/verify`, { supervisorNotes: notes });
      await loadSupervisor(); flash('Verified and locked.');
    } catch (err) { setError(getErrorMessage(err)); }
  };

  const selectedStatus = statuses.find(s => s.code === form.statusCode);

  const tabs = [
    ['mine', 'Mine'],
    ['day', 'Draw'],
    ['verify', `Verify${pendingVerify.length ? ` (${pendingVerify.length})` : ''}`],
    ['rooms', 'Rooms'],
    ['flags', `Flags${flags.filter(f => f.is_active).length ? ` (${flags.filter(f => f.is_active).length})` : ''}`],
    ['report', 'Report'],
    ['settings', 'Settings'],
  ];

  return (
    <PageWrapper active="searches" title="Room Searches">
      {error && <div className="alert-bar danger">{error}</div>}
      {success && <div className="alert-bar success">{success}</div>}

      {canSupervise && (
        <div className="tab-row">
          {tabs.map(([id, lbl]) => (
            <div key={id} className={`tab ${tab === id ? 'active' : ''}`} onClick={() => setTab(id)}>{lbl}</div>
          ))}
        </div>
      )}

      {loading ? <div className="spinner" /> : (
        <>
          {tab === 'mine' && <MySearches mine={mine} lockedCount={lockedCount} onOpen={openSearch} />}
          {tab === 'day' && canSupervise && (
            <DayView date={date} setDate={setDate} dayList={dayList} shifts={shifts}
              staffList={staffList} rooms={rooms} onGenerate={generate} onAssign={assign}
              onForCause={forCause} onDelete={deleteSearch} saving={saving} />
          )}
          {tab === 'verify' && canSupervise && <VerifyQueue list={pendingVerify} onVerify={verify} />}
          {tab === 'rooms' && canSupervise && <RoomsTab rooms={rooms} reload={loadSupervisor} />}
          {tab === 'flags' && canSupervise && <FlagsTab flags={flags} clients={clients} reload={loadSupervisor} flash={flash} />}
          {tab === 'report' && canSupervise && <ReportTab />}
          {tab === 'settings' && canSupervise && <SettingsTab flash={flash} />}
        </>
      )}

      {activeSearch && (
        <SearchModal search={activeSearch} form={form} setForm={setForm} statuses={statuses}
          staffList={staffList} selectedStatus={selectedStatus} saving={saving}
          onSubmit={submitSearch} onDefer={deferSearch} onAmend={addAmendment} onClose={() => setActiveSearch(null)} me={staff} />
      )}
    </PageWrapper>
  );
}

// ── Staff view ──
// Unreleased searches show a count only. Room numbers stay hidden until the
// shift starts, or the search stops being unannounced.
function MySearches({ mine, lockedCount, onOpen }) {
  const released = mine.filter(s => s.is_released);
  const pending = released.filter(s => s.status_code === 'pending');
  const done = released.filter(s => s.status_code !== 'pending');

  if (!mine.length) return <div className="empty-state">No searches assigned to you.</div>;

  return (
    <>
      {lockedCount > 0 && (
        <div className="card" style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--primary)' }}>
            {lockedCount} search{lockedCount === 1 ? '' : 'es'} assigned
          </div>
          <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4 }}>
            Room numbers unlock at the start of your shift
          </div>
        </div>
      )}

      {pending.length > 0 && <div className="section-label">To search</div>}
      {pending.map(s => (
        <div key={s.id} className="card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 15 }}>
                Room {s.room_number}
                {s.room_type === 'common_area' && <span className="badge" style={{ background: '#EEF1F8', color: 'var(--primary)', marginLeft: 8 }}>Common</span>}
                {s.flagged_at_draw && <span className="badge" style={{ background: 'var(--red-light)', color: 'var(--red)', marginLeft: 6 }}>Priority</span>}
              </div>
              <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>
                {s.shift_name}
                {s.deferral_count > 0 && ` · deferred ${s.deferral_count}x`}
              </div>
              {(s.occupant_snapshot || []).length > 0 && (
                <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 4 }}>{occupantText(s.occupant_snapshot)}</div>
              )}
            </div>
            <button className="btn btn-primary btn-sm" onClick={() => onOpen(s.id)}>Start</button>
          </div>
        </div>
      ))}

      {done.length > 0 && <div className="section-label" style={{ marginTop: 20 }}>Logged</div>}
      {done.map(s => (
        <div key={s.id} className="card" style={{ opacity: 0.72 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10 }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14 }}>Room {s.room_number}</div>
              <div style={{ fontSize: 12, color: 'var(--gray-400)' }}>
                {s.status_label || s.status_code}
                {s.contraband_found && ' · finding logged'}
              </div>
            </div>
            {s.is_locked
              ? <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>Locked</span>
              : <button className="btn btn-secondary btn-sm" onClick={() => onOpen(s.id)}>View</button>}
          </div>
        </div>
      ))}
    </>
  );
}

// ── Supervisor: the day's draw ──
function DayView({ date, setDate, dayList, shifts, staffList, rooms, onGenerate, onAssign, onForCause, onDelete, saving }) {
  const [shiftId, setShiftId] = useState('');
  const [fc, setFc] = useState(null);
  const drawn = dayList.find(d => d.draw_pool_size != null);

  const completed = dayList.filter(d => d.counts_as_complete).length;
  const findings = dayList.filter(d => d.contraband_found).length;

  return (
    <>
      <div className="card">
        <div className="field" style={{ marginBottom: 10 }}>
          <label>Date</label>
          <input type="date" value={date} onChange={e => setDate(e.target.value)} />
        </div>
        <div className="field" style={{ marginBottom: 12 }}>
          <label>Shift</label>
          <select value={shiftId} onChange={e => setShiftId(e.target.value)}>
            <option value="">Select shift…</option>
            {shifts.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" style={{ width: '100%' }} disabled={!shiftId || saving} onClick={() => onGenerate(shiftId)}>
          {saving ? 'Drawing…' : 'Generate draw'}
        </button>
        <button className="btn btn-secondary" style={{ width: '100%', marginTop: 8 }}
          onClick={() => setFc(fc ? null : { roomId: '', reason: '', shiftId: shiftId || '' })}>
          {fc ? 'Cancel' : 'Add a for-cause search'}
        </button>

        {fc && (
          <>
            <hr className="divider" />
            <div style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 8 }}>
              A for-cause search is targeted and recorded separately from the random draw,
              so it never dilutes the evidence that the random program is random.
            </div>
            <div className="field">
              <label>Room</label>
              <select value={fc.roomId} onChange={e => setFc(f => ({ ...f, roomId: e.target.value }))}>
                <option value="">Select room…</option>
                {(rooms || []).filter(r => r.is_active).map(r => (
                  <option key={r.id} value={r.id}>
                    {r.room_type === 'common_area' ? r.room_number : `Room ${r.room_number}`}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label>Written justification <span style={{ color: 'var(--red)' }}>· required</span></label>
              <textarea value={fc.reason} onChange={e => setFc(f => ({ ...f, reason: e.target.value }))} rows={3}
                placeholder="Why is this room being searched specifically?" />
            </div>
            <div className="field">
              <label>Shift</label>
              <select value={fc.shiftId} onChange={e => setFc(f => ({ ...f, shiftId: e.target.value }))}>
                <option value="">Unassigned</option>
                {shifts.map(sh => <option key={sh.id} value={sh.id}>{sh.name}</option>)}
              </select>
            </div>
            <button className="btn btn-primary btn-sm" disabled={!fc.roomId || fc.reason.trim().length < 10 || saving}
              onClick={async () => {
                const ok = await onForCause({ roomId: fc.roomId, reason: fc.reason.trim(), shiftId: fc.shiftId || null, searchDate: date });
                if (ok) setFc(null);
              }}>
              Create for-cause search
            </button>
          </>
        )}
      </div>

      {dayList.length > 0 && (
        <div className="stats-grid">
          <div className="stat-card"><div className="num">{dayList.length}</div><div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Drawn</div></div>
          <div className="stat-card"><div className="num">{completed}</div><div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Completed</div></div>
          <div className="stat-card"><div className="num" style={{ color: findings ? 'var(--accent-dark)' : undefined }}>{findings}</div><div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Findings</div></div>
        </div>
      )}

      {drawn && (
        <div className="card" style={{ background: '#F7F9FC' }}>
          <div className="section-label" style={{ marginBottom: 6 }}>Draw audit</div>
          <div style={{ fontSize: 12, color: 'var(--gray-600)', lineHeight: 1.7 }}>
            Eligible pool {drawn.draw_pool_size} · excluded by cooldown {drawn.draw_excluded_count} · {drawn.draw_method}
            {drawn.released_at && <><br />Released to staff {new Date(drawn.released_at).toLocaleString()}</>}
          </div>
        </div>
      )}

      {!dayList.length
        ? <div className="empty-state">No searches drawn for this date.</div>
        : dayList.map(s => (
          <div key={s.id} className="card">
            <div style={{ fontWeight: 600, fontSize: 15 }}>
              Room {s.room_number}
              {s.search_type === 'for_cause' && <span className="badge" style={{ background: 'var(--amber-light)', color: 'var(--amber)', marginLeft: 8 }}>For cause</span>}
              {s.flagged_at_draw && <span className="badge" style={{ background: 'var(--red-light)', color: 'var(--red)', marginLeft: 6 }}>Priority</span>}
            </div>
            <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 2 }}>
              {s.shift_name} · tier {s.search_tier} · {s.status_label || s.status_code}
              {s.deferral_count > 0 && ` · deferred ${s.deferral_count}x`}
            </div>
            {(s.occupant_snapshot || []).length > 0 && (
              <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 4 }}>
                At draw: {occupantText(s.occupant_snapshot)}
              </div>
            )}
            <div className="field" style={{ marginTop: 10, marginBottom: 0 }}>
              <label>Assigned to</label>
              <select value={s.assigned_staff_id || ''} disabled={s.is_locked} onChange={e => onAssign(s.id, e.target.value)}>
                <option value="">Unassigned</option>
                {staffList.map(st => <option key={st.id} value={st.id}>{st.first_name} {st.last_name}</option>)}
              </select>
            </div>
            {!s.completed_at && !s.is_locked && (
              <button className="btn btn-secondary btn-sm" style={{ marginTop: 8 }} onClick={() => onDelete(s.id)}>
                Remove from draw
              </button>
            )}
          </div>
        ))}
    </>
  );
}

// ── Supervisor: verification queue ──
function VerifyQueue({ list, onVerify }) {
  const [notesFor, setNotesFor] = useState(null);
  const [notes, setNotes] = useState('');

  if (!list.length) return <div className="empty-state">Nothing awaiting verification.</div>;

  return (
    <>
      {list.map(s => (
        <div key={s.id} className="log-entry late">
          <div className="le-client">Room {s.room_number} · finding logged</div>
          <div className="le-detail">
            {s.contraband_description}
            {s.disposition && <> · <strong>{s.disposition}</strong></>}
          </div>
          <div className="le-time" style={{ marginTop: 4 }}>
            {s.completed_by_name || '—'}
            {s.client_present !== null && ` · client ${s.client_present ? 'present' : 'not present'}`}
            {s.completed_at && ` · ${new Date(s.completed_at).toLocaleString()}`}
          </div>
          {!s.incident_report_filed && (
            <div style={{ fontSize: 12, color: 'var(--amber)', marginTop: 4 }}>Incident report not marked as filed</div>
          )}
          <button className="btn btn-primary btn-sm" style={{ marginTop: 10 }} onClick={() => { setNotesFor(s.id); setNotes(''); }}>
            Verify &amp; lock
          </button>
        </div>
      ))}

      {notesFor && (
        <div className="modal-overlay" onClick={() => setNotesFor(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Verify search</h2>
            <p style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 12 }}>
              Verifying locks this record. Later corrections must be added as amendments.
            </p>
            <div className="field">
              <label>Supervisor notes (optional)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" onClick={() => { onVerify(notesFor, notes); setNotesFor(null); }}>Verify &amp; lock</button>
              <button className="btn btn-secondary" onClick={() => setNotesFor(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ── Supervisor: rooms and tiering ──
function RoomsTab({ rooms, reload }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ roomNumber: '', roomType: 'client_room', searchTier: 1, displayName: '' });
  const [err, setErr] = useState('');

  const save = async () => {
    setErr('');
    try {
      await api.post('/searches/rooms', form);
      setAdding(false);
      setForm({ roomNumber: '', roomType: 'client_room', searchTier: 1, displayName: '' });
      reload();
    } catch (e) { setErr(getErrorMessage(e)); }
  };

  const patch = async (room, body) => {
    try { await api.patch(`/searches/rooms/${room.id}`, body); reload(); }
    catch (e) { setErr(getErrorMessage(e)); }
  };

  const tier1 = rooms.filter(r => r.search_tier === 1);
  const tier2 = rooms.filter(r => r.search_tier === 2);

  const Row = ({ r }) => (
    <div style={{ padding: '10px 0', borderBottom: '1px solid #F1F3F6' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0 }}>
          <span style={{ fontWeight: 600, fontSize: 14 }}>
            {r.room_type === 'common_area' ? r.room_number : `Room ${r.room_number}`}
          </span>
          {r.display_name && <span style={{ fontSize: 12, color: 'var(--gray-400)' }}> · {r.display_name}</span>}
          <div className="le-time">
            {r.current_occupants > 0 ? `${r.current_occupants} occupant${r.current_occupants === 1 ? '' : 's'}` : 'Vacant'}
            {r.last_searched && ` · last searched ${formatDate(r.last_searched)}`}
            {!r.is_active && ' · inactive'}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <select
            value={r.search_tier}
            onChange={e => {
              const tier = parseInt(e.target.value, 10);
              patch(r, { searchTier: tier, roomType: tier === 2 ? 'common_area' : 'client_room' });
            }}
            style={{ width: 'auto', fontSize: 12, padding: '5px 8px' }}
          >
            <option value={1}>Tier 1 · client room</option>
            <option value={2}>Tier 2 · common area</option>
          </select>
          <button className={`btn btn-sm ${r.is_active ? 'btn-secondary' : 'btn-primary'}`} onClick={() => patch(r, { isActive: !r.is_active })}>
            {r.is_active ? 'Active' : 'Off'}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {err && <div className="alert-bar danger">{err}</div>}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ fontSize: 14 }}>Rooms ({rooms.length})</strong>
          <button className="btn btn-primary btn-sm" onClick={() => setAdding(!adding)}>{adding ? 'Cancel' : '+ Add'}</button>
        </div>

        {adding && (
          <>
            <hr className="divider" />
            <div className="field">
              <label>Room number or name</label>
              <input value={form.roomNumber} onChange={e => setForm(f => ({ ...f, roomNumber: e.target.value }))} placeholder="e.g. 11 or Gym" />
            </div>
            <div className="field">
              <label>Type</label>
              <select value={form.roomType} onChange={e => setForm(f => ({ ...f, roomType: e.target.value, searchTier: e.target.value === 'common_area' ? 2 : 1 }))}>
                <option value="client_room">Client room (tier 1)</option>
                <option value="common_area">Common area (tier 2)</option>
              </select>
            </div>
            <button className="btn btn-primary btn-sm" onClick={save}>Save room</button>
          </>
        )}
      </div>

      <div className="card">
        <div className="section-label">Tier 1 · Client rooms ({tier1.length})</div>
        <div style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 4 }}>Searched whether or not the room is occupied.</div>
        {tier1.length ? tier1.map(r => <Row key={r.id} r={r} />) : <div className="le-time" style={{ padding: '8px 0' }}>None.</div>}
      </div>

      <div className="card">
        <div className="section-label">Tier 2 · Common areas ({tier2.length})</div>
        <div style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 4 }}>Bathrooms, living room, kitchen, gym. Drawn from a separate pool.</div>
        {tier2.length ? tier2.map(r => <Row key={r.id} r={r} />) : <div className="le-time" style={{ padding: '8px 0' }}>None yet — add common areas above.</div>}
      </div>
    </>
  );
}

// ── Completion form ──
function SearchModal({ search, form, setForm, statuses, staffList, selectedStatus, saving, onSubmit, onDefer, onAmend, onClose, me }) {
  const [amendText, setAmendText] = useState('');
  const [deferring, setDeferring] = useState(false);
  const [deferReason, setDeferReason] = useState('');
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const locked = search.is_locked;
  const completes = selectedStatus?.counts_as_complete;

  const Toggle = ({ value, onPick, options }) => (
    <div style={{ display: 'flex', gap: 6 }}>
      {options.map(([v, l]) => (
        <button key={l} type="button" onClick={() => onPick(v)}
          className={`tab ${value === v ? 'active' : ''}`} style={{ flex: 1 }}>{l}</button>
      ))}
    </div>
  );

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={e => e.stopPropagation()} style={{ maxHeight: '88vh', overflowY: 'auto' }}>
        <h2 style={{ marginBottom: 2 }}>
          {search.room_type === 'common_area' ? search.room_number : `Room ${search.room_number}`}
        </h2>
        <div style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 14 }}>
          {search.shift_name} · {formatDate(search.search_date)}
          {search.search_type === 'for_cause' && ' · for cause'}
        </div>

        {search.for_cause_reason && (
          <div className="alert-bar" style={{ background: 'var(--amber-light)', color: 'var(--amber)' }}>
            <strong>Justification:</strong> {search.for_cause_reason}
          </div>
        )}

        {(search.occupant_snapshot || []).length > 0 && (
          <div style={{ fontSize: 13, color: 'var(--gray-600)', marginBottom: 12 }}>
            Occupants at draw: {occupantText(search.occupant_snapshot)}
          </div>
        )}

        {deferring ? (
          <>
            <div className="field">
              <label>Why is this being deferred to the next shift?</label>
              <textarea value={deferReason} onChange={e => setDeferReason(e.target.value)} rows={3} autoFocus />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" disabled={!deferReason.trim() || saving} onClick={() => onDefer(deferReason)}>
                {saving ? 'Saving…' : 'Confirm deferral'}
              </button>
              <button className="btn btn-secondary" onClick={() => setDeferring(false)}>Back</button>
            </div>
          </>
        ) : locked ? (
          <>
            <div className="alert-bar" style={{ background: 'var(--gray-100, #F3F4F6)', color: 'var(--gray-600)' }}>
              Verified and locked. Corrections must be added as an amendment.
            </div>
            {(search.amendments || []).length > 0 && (
              <>
                <div className="section-label">Amendments</div>
                {search.amendments.map(a => (
                  <div key={a.id} className="le-time" style={{ marginBottom: 6 }}>
                    {a.amendment}
                    <br />— {a.amended_by_name || 'unknown'}, {formatDate(a.created_at)}
                  </div>
                ))}
              </>
            )}
            <div className="field">
              <label>Add an amendment</label>
              <textarea value={amendText} onChange={e => setAmendText(e.target.value)} rows={3}
                placeholder="Corrections are appended, never edited over the original." />
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn btn-primary" disabled={!amendText.trim()}
                onClick={async () => { if (await onAmend(search.id, amendText.trim())) { setAmendText(''); onClose(); } }}>
                Add amendment
              </button>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </>
        ) : (
          <>
            <div className="field">
              <label>Outcome</label>
              <select value={form.statusCode} onChange={e => set('statusCode', e.target.value)}>
                {statuses.filter(s => s.code !== 'pending').map(s => (
                  <option key={s.code} value={s.code}>{s.label}</option>
                ))}
              </select>
              {selectedStatus?.description && (
                <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>{selectedStatus.description}</div>
              )}
            </div>

            {completes && (
              <>
                <div className="field">
                  <label>Client present during search</label>
                  <Toggle value={form.clientPresent} onPick={v => set('clientPresent', v)} options={[[true, 'Yes'], [false, 'No']]} />
                </div>


                <div className="field">
                  <label>Contraband found</label>
                  <Toggle value={form.contrabandFound} onPick={v => set('contrabandFound', v)} options={[[false, 'None'], [true, 'Yes']]} />
                </div>

                {form.contrabandFound && (
                  <>
                    <div className="field">
                      <label>Description</label>
                      <textarea value={form.contrabandDescription} onChange={e => set('contrabandDescription', e.target.value)}
                        rows={2} placeholder="What was found and where" />
                    </div>
                    <div className="field">
                      <label>Disposition</label>
                      <select value={form.disposition} onChange={e => set('disposition', e.target.value)}>
                        <option>Secured for discharge</option>
                        <option>Destroyed</option>
                        <option>Released to law enforcement</option>
                        <option>Returned to client</option>
                      </select>
                    </div>
                    <div className="field">
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <input type="checkbox" checked={!!form.incidentReportFiled}
                          onChange={e => set('incidentReportFiled', e.target.checked)} style={{ width: 'auto' }} />
                        Incident report filed
                      </label>
                    </div>
                  </>
                )}
              </>
            )}

            <div className="field">
              <label>
                Note {selectedStatus?.requires_note && <span style={{ color: 'var(--red)' }}>· required</span>}
              </label>
              <textarea value={form.statusNote} onChange={e => set('statusNote', e.target.value)} rows={2} />
            </div>

            {(search.deferrals || []).length > 0 && (
              <>
                <hr className="divider" />
                <div className="section-label">Deferral history</div>
                {search.deferrals.map(d => (
                  <div key={d.id} className="le-time" style={{ marginBottom: 4 }}>
                    {formatDate(d.from_date)} {d.from_shift} → {formatDate(d.to_date)} {d.to_shift}: {d.reason}
                  </div>
                ))}
              </>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 16, flexWrap: 'wrap' }}>
              <button className="btn btn-primary" disabled={saving} onClick={onSubmit}>
                {saving ? 'Saving…' : 'Submit search'}
              </button>
              {search.status_code === 'pending' && (
                <button className="btn btn-secondary" onClick={() => setDeferring(true)}>Defer</button>
              )}
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ── Supervisor: client priority flags ──
// A flag raises how often that client's room comes up in the random draw.
// It expires on its own and does not carry across admissions — a returning
// client gets re-flagged with fresh reasoning, not a stale one from last time.
function FlagsTab({ flags, clients, reload, flash }) {
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ clientId: '', reason: '', days: 14 });
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  const active = flags.filter(f => f.is_active);
  const past = flags.filter(f => !f.is_active);

  const save = async () => {
    if (!form.clientId) { setErr('Select a client.'); return; }
    if (form.reason.trim().length < 10) { setErr('Give a reason of at least 10 characters.'); return; }
    setSaving(true); setErr('');
    try {
      await api.post('/searches/flags', {
        clientId: form.clientId,
        reason: form.reason.trim(),
        days: parseInt(form.days, 10) || 14
      });
      setAdding(false);
      setForm({ clientId: '', reason: '', days: 14 });
      reload();
      flash('Flag set.');
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setSaving(false); }
  };

  const clear = async (id) => {
    try { await api.patch(`/searches/flags/${id}/clear`); reload(); flash('Flag cleared.'); }
    catch (e) { setErr(getErrorMessage(e)); }
  };

  const daysLeft = (expiresAt) =>
    Math.max(0, Math.ceil((new Date(expiresAt) - Date.now()) / 86400000));

  const alreadyFlagged = new Set(active.map(f => f.client_id));

  return (
    <>
      {err && <div className="alert-bar danger">{err}</div>}

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ fontSize: 14 }}>Priority flags ({active.length} active)</strong>
          <button className="btn btn-primary btn-sm" onClick={() => { setAdding(!adding); setErr(''); }}>
            {adding ? 'Cancel' : '+ Flag a client'}
          </button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4 }}>
          A flagged client's room is drawn roughly 3× more often while the flag is active.
        </div>

        {adding && (
          <>
            <hr className="divider" />
            <div className="field">
              <label>Client</label>
              <select value={form.clientId} onChange={e => setForm(f => ({ ...f, clientId: e.target.value }))}>
                <option value="">Select client…</option>
                {clients.filter(c => !alreadyFlagged.has(c.id)).map(c => (
                  <option key={c.id} value={c.id}>
                    {c.first_name} {c.last_name}{c.room_number ? ` — ${c.room_number}` : ''}
                  </option>
                ))}
              </select>
            </div>

            <div className="field">
              <label>Reason</label>
              <textarea
                value={form.reason}
                onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                rows={3}
                placeholder="e.g. Two contraband findings this admission"
              />
              <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 4 }}>
                Write this as something you'd be comfortable reading aloud in a grievance review.
                It stays on the record after the flag expires.
              </div>
            </div>

            <div className="field">
              <label>Expires after</label>
              <select value={form.days} onChange={e => setForm(f => ({ ...f, days: e.target.value }))}>
                <option value={7}>7 days</option>
                <option value={14}>14 days</option>
                <option value={30}>30 days</option>
              </select>
            </div>

            <button className="btn btn-primary btn-sm" disabled={saving} onClick={save}>
              {saving ? 'Saving…' : 'Set flag'}
            </button>
          </>
        )}
      </div>

      {active.length > 0 && (
        <div className="card">
          <div className="section-label">Active</div>
          {active.map(f => (
            <div key={f.id} style={{ padding: '10px 0', borderBottom: '1px solid #F1F3F6' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>
                    {f.client_name}
                    {f.room_number && <span style={{ color: 'var(--gray-400)', fontWeight: 400 }}> · {f.room_number}</span>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--gray-600)', marginTop: 3 }}>{f.reason}</div>
                  <div className="le-time" style={{ marginTop: 4 }}>
                    Set by {f.set_by_name || '—'} · expires in {daysLeft(f.expires_at)} day{daysLeft(f.expires_at) === 1 ? '' : 's'}
                  </div>
                </div>
                <button className="btn btn-secondary btn-sm" onClick={() => clear(f.id)}>Clear</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {past.length > 0 && (
        <div className="card">
          <div className="section-label">Expired &amp; cleared</div>
          <div style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 6 }}>
            Kept on the record — the searches a flag caused are already in the history.
          </div>
          {past.slice(0, 15).map(f => (
            <div key={f.id} style={{ padding: '8px 0', borderBottom: '1px solid #F1F3F6', opacity: 0.7 }}>
              <div style={{ fontSize: 13 }}>{f.client_name}</div>
              <div className="le-time">
                {f.reason} · {f.cleared_at ? 'cleared' : 'expired'} {new Date(f.cleared_at || f.expires_at).toLocaleDateString()}
              </div>
            </div>
          ))}
        </div>
      )}

      {flags.length === 0 && !adding && (
        <div className="empty-state">No priority flags have been set.</div>
      )}
    </>
  );
}

// ── Supervisor: internal search report ──
// Deliberately separate from the state/insurance log reports. Not submitted to
// regulators or payers.
function ReportTab() {
  const [from, setFrom] = useState(() => new Date(Date.now() - 29 * 864e5).toISOString().slice(0, 10));
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');

  const run = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const r = await api.get(`/searches/report?fromDate=${from}&toDate=${to}`);
      setData(r.data);
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setLoading(false); }
  }, [from, to]);

  useEffect(() => { run(); }, [run]);

  const s = data?.summary;

  return (
    <>
      {err && <div className="alert-bar danger">{err}</div>}
      <div className="card">
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>From</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
          </div>
          <div className="field" style={{ marginBottom: 0 }}>
            <label>To</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} />
          </div>
        </div>
        <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 8 }}>
          Internal use. Room search records are not part of the state or insurance log reports.
        </div>
      </div>

      {loading ? <div className="spinner" /> : s && (
        <>
          <div className="stats-grid">
            <div className="stat-card"><div className="num">{s.total}</div><div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Searches</div></div>
            <div className="stat-card"><div className="num">{s.completed}</div><div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Completed</div></div>
            <div className="stat-card"><div className="num" style={{ color: s.with_findings ? 'var(--accent-dark)' : undefined }}>{s.with_findings}</div><div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Findings</div></div>
            <div className="stat-card"><div className="num">{s.distinct_rooms}</div><div style={{ fontSize: 11, color: 'var(--gray-400)' }}>Rooms hit</div></div>
          </div>

          <div className="card">
            <div className="section-label">By outcome</div>
            {(data.byStatus || []).map(r => (
              <div key={r.status_code} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F1F3F6', fontSize: 13 }}>
                <span>{r.label}</span>
                <span style={{ color: 'var(--gray-400)' }}>{r.n}{s.total > 0 ? ` · ${Math.round((r.n / s.total) * 100)}%` : ''}</span>
              </div>
            ))}
            <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 8 }}>
              {s.refused} refused · {s.deferred} deferred · {s.for_cause} for cause
            </div>
          </div>

          <div className="card">
            <div className="section-label">Room coverage</div>
            <div style={{ fontSize: 12, color: 'var(--gray-400)', marginBottom: 6 }}>
              Least-searched first. A room the draw keeps missing is the gap worth closing.
            </div>
            {(data.coverage || []).map(r => (
              <div key={r.room_number} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #F1F3F6', fontSize: 13 }}>
                <span>
                  {r.room_type === 'common_area' ? r.room_number : `Room ${r.room_number}`}
                  <span style={{ color: 'var(--gray-400)', fontSize: 11 }}> · tier {r.search_tier}</span>
                </span>
                <span style={{ color: r.searches === 0 ? 'var(--red)' : 'var(--gray-400)' }}>
                  {r.searches} search{r.searches === 1 ? '' : 'es'}
                  {r.last_searched ? ` · last ${formatDate(r.last_searched)}` : ' · never'}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </>
  );
}

// ── Supervisor: search settings and outcome statuses ──
function SettingsTab({ flash }) {
  const [settings, setSettings] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [edits, setEdits] = useState({});
  const [adding, setAdding] = useState(false);
  const [newStatus, setNewStatus] = useState({ code: '', label: '', description: '', requiresNote: true, countsAsComplete: false, triggersDeferral: false, notifySupervisor: false });
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [se, st] = await Promise.all([
        api.get('/searches/settings'),
        api.get('/searches/statuses?all=true'),
      ]);
      setSettings(se.data || []);
      setStatuses(st.data || []);
      setEdits({});
    } catch (e) { setErr(getErrorMessage(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const saveSetting = async (key) => {
    setErr('');
    try {
      await api.patch(`/searches/settings/${key}`, { value: String(edits[key]) });
      await load(); flash('Setting updated.');
    } catch (e) { setErr(getErrorMessage(e)); }
  };

  const patchStatus = async (code, body) => {
    setErr('');
    try { await api.patch(`/searches/statuses/${code}`, body); await load(); }
    catch (e) { setErr(getErrorMessage(e)); }
  };

  const addStatus = async () => {
    if (!newStatus.code || !newStatus.label) { setErr('Code and label are required.'); return; }
    setErr('');
    try {
      await api.post('/searches/statuses', newStatus);
      setAdding(false);
      setNewStatus({ code: '', label: '', description: '', requiresNote: true, countsAsComplete: false, triggersDeferral: false, notifySupervisor: false });
      await load(); flash('Status added.');
    } catch (e) { setErr(getErrorMessage(e)); }
  };

  if (loading) return <div className="spinner" />;

  const flagWeight = settings.find(s => s.key === 'flag_weight_multiplier');

  return (
    <>
      {err && <div className="alert-bar danger">{err}</div>}

      <div className="card">
        <div className="section-label">Draw settings</div>
        {settings.map(s => {
          const val = edits[s.key] !== undefined ? edits[s.key] : s.value;
          const dirty = edits[s.key] !== undefined && String(edits[s.key]) !== String(s.value);
          return (
            <div key={s.key} style={{ padding: '10px 0', borderBottom: '1px solid #F1F3F6' }}>
              <div style={{ fontSize: 13, color: '#1F2937' }}>{s.label || s.key}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: 6 }}>
                {s.value_type === 'bool' ? (
                  <select value={val} onChange={e => setEdits(x => ({ ...x, [s.key]: e.target.value }))} style={{ width: 'auto' }}>
                    <option value="true">On</option>
                    <option value="false">Off</option>
                  </select>
                ) : (
                  <input
                    type={s.value_type === 'int' ? 'number' : 'text'}
                    value={val}
                    onChange={e => setEdits(x => ({ ...x, [s.key]: e.target.value }))}
                    style={{ width: 120 }}
                  />
                )}
                {dirty && <button className="btn btn-primary btn-sm" onClick={() => saveSetting(s.key)}>Save</button>}
                <span style={{ fontSize: 11, color: 'var(--gray-400)' }}>{s.key}</span>
              </div>
            </div>
          );
        })}
        {flagWeight && Number(edits.flag_weight_multiplier ?? flagWeight.value) > 5 && (
          <div className="alert-bar" style={{ background: 'var(--amber-light)', color: 'var(--amber)', marginTop: 10 }}>
            A weight above 5 means a flagged client's room comes up far more often than anyone else's.
            That stops reading as prioritisation and starts reading as targeting. Pick a number you'd be comfortable explaining.
          </div>
        )}
      </div>

      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <strong style={{ fontSize: 14 }}>Outcome statuses ({statuses.filter(s => s.is_active).length} active)</strong>
          <button className="btn btn-primary btn-sm" onClick={() => setAdding(!adding)}>{adding ? 'Cancel' : '+ Add'}</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--gray-400)', marginTop: 4 }}>
          What staff can pick when logging a search, and what each choice does.
        </div>

        {adding && (
          <>
            <hr className="divider" />
            <div className="field"><label>Label</label>
              <input value={newStatus.label} onChange={e => setNewStatus(s => ({ ...s, label: e.target.value, code: e.target.value.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '') }))} placeholder="e.g. Room under maintenance" />
            </div>
            <div className="field"><label>Description</label>
              <input value={newStatus.description} onChange={e => setNewStatus(s => ({ ...s, description: e.target.value }))} placeholder="Shown under the picker" />
            </div>
            {[['requiresNote', 'Requires a note'], ['countsAsComplete', 'Counts as a completed search'], ['triggersDeferral', 'Pushes to the next shift'], ['notifySupervisor', 'Texts a supervisor']].map(([k, lbl]) => (
              <div className="field" key={k} style={{ marginBottom: 6 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input type="checkbox" checked={newStatus[k]} onChange={e => setNewStatus(s => ({ ...s, [k]: e.target.checked }))} style={{ width: 'auto' }} />
                  {lbl}
                </label>
              </div>
            ))}
            <button className="btn btn-primary btn-sm" style={{ marginTop: 8 }} onClick={addStatus}>Add status</button>
          </>
        )}

        <hr className="divider" />
        {statuses.map(st => (
          <div key={st.code} style={{ padding: '10px 0', borderBottom: '1px solid #F1F3F6', opacity: st.is_active ? 1 : 0.55 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 600 }}>
                  {st.label}
                  {st.is_system && <span className="badge" style={{ background: '#EEF1F8', color: 'var(--primary)', marginLeft: 8 }}>System</span>}
                </div>
                <div className="le-time">
                  {[st.requires_note && 'note required', st.counts_as_complete && 'counts as complete',
                    st.triggers_deferral && 'defers', st.notify_supervisor && 'notifies supervisor']
                    .filter(Boolean).join(' · ') || 'no special behaviour'}
                </div>
              </div>
              {!st.is_system && (
                <button className="btn btn-secondary btn-sm" onClick={() => patchStatus(st.code, { isActive: !st.is_active })}>
                  {st.is_active ? 'Disable' : 'Enable'}
                </button>
              )}
            </div>
          </div>
        ))}
        <div style={{ fontSize: 11, color: 'var(--gray-400)', marginTop: 8 }}>
          Pending and Completed are system statuses — reporting maths depends on them, so they can be renamed but not disabled.
        </div>
      </div>
    </>
  );
}
